import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Alert, Spinner, Row, Col, Button } from 'react-bootstrap';
import { FiArrowRight, FiActivity } from 'react-icons/fi';
import { useTranslation } from '../../../hooks/useTranslation';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import { useTodayDashboard } from './hooks/useDashboardData';
import DashboardPageHeader from './components/DashboardPageHeader';
import OperationsNav from './components/OperationsNav';
import DashboardSummaryCards from './components/DashboardSummaryCards';
import DashboardFilters from './components/DashboardFilters';
import ForecastTable from './components/ForecastTable';
import ForecastListPagination from './components/ForecastListPagination';
import ObservationTable from './components/ObservationTable';
import OperationsSectionHeader from './components/OperationsSectionHeader';
import { buildFilterSearchParams } from './utils/filterHelpers';
import { formatReferenceDate } from './utils/dateHelpers';
import { DEFAULT_FORECAST_PAGE_SIZE } from './utils/dashboardConstants';
import { clampForecastLimit } from '../../../services/dashboardOperations';
import 'bootstrap/dist/css/bootstrap.css';
import './operations-dashboard.css';

const SummarySkeleton = () => (
    <Row className="g-3 mb-4">
        {Array.from({ length: 6 }).map((_, index) => (
            <Col key={index} xs={6} md={4} xl={2}>
                <Card className="admin-ds-section-card h-100">
                    <Card.Body className="py-4">
                        <div className="placeholder-glow">
                            <span className="placeholder col-6 mb-2" />
                            <span className="placeholder col-4" />
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        ))}
    </Row>
);

const businessFilterKey = (filters) => JSON.stringify({
    run_date: filters.run_date,
    policy_code: filters.policy_code,
    partner_id: filters.partner_id,
    company_name: filters.company_name,
    distribution_type: filters.distribution_type,
    include_completed: filters.include_completed,
});

const OperationsDashboard = () => {
    const { t } = useTranslation();
    const { filters, setFilter, resetFilters, hasActiveFilters } = useDashboardFilters();
    const [forecastPage, setForecastPage] = useState(1);
    const [forecastPageSize, setForecastPageSize] = useState(DEFAULT_FORECAST_PAGE_SIZE);
    const [appliedFilterKey, setAppliedFilterKey] = useState(() => businessFilterKey(filters));

    const nextFilterKey = businessFilterKey(filters);
    if (nextFilterKey !== appliedFilterKey) {
        setAppliedFilterKey(nextFilterKey);
        if (forecastPage !== 1) {
            setForecastPage(1);
        }
    }

    const effectivePage = nextFilterKey !== appliedFilterKey ? 1 : forecastPage;

    const queryFilters = useMemo(() => ({
        ...filters,
        forecast_skip: (effectivePage - 1) * forecastPageSize,
        forecast_limit: forecastPageSize,
    }), [filters, effectivePage, forecastPageSize]);

    const { data, loading, error, refetch, isError } = useTodayDashboard(queryFilters);

    const forecastRef = useRef(null);
    const claimsRef = useRef(null);
    const fntRef = useRef(null);

    const filterQuery = useMemo(() => buildFilterSearchParams(filters), [filters]);

    const forecastMeta = data?.forecast;
    const forecastItems = forecastMeta?.items || [];
    const forecastTotal = forecastMeta?.total ?? 0;
    const forecastApiPage = forecastMeta?.page ?? effectivePage;
    const forecastTotalPages = forecastMeta?.total_pages ?? 0;

    // Page hors bornes après resserrement des filtres → revenir à la page 1
    useEffect(() => {
        if (!data?.forecast) return;
        const { total_pages: totalPages } = data.forecast;
        if (totalPages > 0 && forecastPage > totalPages) {
            setForecastPage(1);
        }
    }, [data, forecastPage]);

    const handleCardClick = (section) => {
        const refs = {
            forecast: forecastRef,
            observation_claims: claimsRef,
            observation_fnt: fntRef,
        };
        refs[section]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleForecastPageChange = (page) => {
        setForecastPage(Math.max(1, page));
    };

    const handleForecastPageSizeChange = (size) => {
        setForecastPageSize(clampForecastLimit(size));
        setForecastPage(1);
    };

    const claimsItems = data?.observation?.claims?.items || [];
    const fntItems = data?.observation?.forecast_not_triggered?.items || [];

    const isEmpty = data
        && data.summary?.forecast_total === 0
        && data.summary?.observation_claims_total === 0
        && (data.summary?.observation_forecast_not_triggered_total ?? 0) === 0;

    if (isError && !data) {
        return (
            <div className="dashboard-page">
                <Alert variant="danger">
                    <Alert.Heading>Erreur de chargement</Alert.Heading>
                    <p className="mb-3">{error}</p>
                    <Button variant="outline-danger" size="sm" onClick={refetch}>
                        Réessayer
                    </Button>
                </Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <Card className="admin-ds-main-card">
                <Card.Body>
                    <DashboardPageHeader
                        title={t('pages.operationsDashboard.title')}
                        icon={FiActivity}
                        referenceDate={data?.reference_date}
                        currentHourParis={data?.current_hour_paris}
                        metadata={data?.metadata}
                        onRefresh={refetch}
                        refreshing={loading}
                    />

                    <OperationsNav />

                    {loading && !data ? (
                        <SummarySkeleton />
                    ) : (
                        <DashboardSummaryCards
                            summary={data?.summary}
                            onCardClick={handleCardClick}
                        />
                    )}

                    <DashboardFilters
                        filters={filters}
                        onFilterChange={setFilter}
                        onReset={resetFilters}
                        hasActiveFilters={hasActiveFilters}
                        visibleFilters={[
                            'run_date',
                            'policy_code',
                            'partner_id',
                            'company_name',
                            'distribution_type',
                            'include_completed',
                        ]}
                    />

                    {isEmpty && data && (
                        <Alert variant="info" className="mb-4">
                            <Alert.Heading>Aucune policy à contrôler</Alert.Heading>
                            <p className="mb-0">
                                Aucune policy éligible pour le {formatReferenceDate(data.reference_date)}.
                            </p>
                        </Alert>
                    )}

                    <section ref={forecastRef} className="mb-3">
                        <Card className="admin-ds-section-card">
                            <Card.Header>
                                <OperationsSectionHeader
                                    title="Forecast"
                                    count={data?.summary?.forecast_total}
                                    viewAllLink={(
                                        <Button
                                            variant="link"
                                            size="sm"
                                            as={Link}
                                            to={`/dashboard/operations/forecast${filterQuery}`}
                                            className="p-0"
                                        >
                                            Voir tout <FiArrowRight />
                                        </Button>
                                    )}
                                />
                            </Card.Header>
                            <Card.Body className="p-0">
                                <ForecastTable
                                    items={forecastItems}
                                    loading={loading}
                                    compact
                                    emptyMessage="Aucun forecast planifié…"
                                    emptySubMessage="Tous les checks forecast sont à jour pour les filtres sélectionnés."
                                />
                                <ForecastListPagination
                                    page={loading ? effectivePage : forecastApiPage}
                                    totalPages={forecastTotalPages}
                                    total={forecastTotal}
                                    skip={loading
                                        ? (effectivePage - 1) * forecastPageSize
                                        : (forecastMeta?.skip ?? 0)}
                                    limit={forecastMeta?.limit ?? forecastPageSize}
                                    pageSize={forecastPageSize}
                                    onPageChange={handleForecastPageChange}
                                    onPageSizeChange={handleForecastPageSizeChange}
                                    disabled={loading}
                                />
                            </Card.Body>
                        </Card>
                    </section>

                    <section ref={claimsRef} className="mb-3">
                        <Card className="admin-ds-section-card">
                            <Card.Header>
                                <OperationsSectionHeader
                                    title="Observation — Sinistres"
                                    count={data?.summary?.observation_claims_total}
                                    viewAllLink={(
                                        <Button
                                            variant="link"
                                            size="sm"
                                            as={Link}
                                            to={`/dashboard/operations/observation${buildFilterSearchParams(filters, { scope: 'claims' })}`}
                                            className="p-0"
                                        >
                                            Voir tout <FiArrowRight />
                                        </Button>
                                    )}
                                />
                            </Card.Header>
                            <Card.Body className="p-0">
                                <ObservationTable
                                    claims={claimsItems}
                                    forecastNotTriggered={[]}
                                    loading={loading && !data}
                                    scope="claims"
                                    compact
                                    emptySubMessage="Aucune observation sinistre requise pour cette date."
                                />
                            </Card.Body>
                        </Card>
                    </section>

                    <section ref={fntRef} className="mb-0">
                        <Card className="admin-ds-section-card">
                            <Card.Header>
                                <OperationsSectionHeader
                                    title="Observation — Non déclenchés"
                                    count={data?.summary?.observation_forecast_not_triggered_total}
                                    viewAllLink={(
                                        <Button
                                            variant="link"
                                            size="sm"
                                            as={Link}
                                            to={`/dashboard/operations/observation${buildFilterSearchParams(filters, { scope: 'forecast_not_triggered' })}`}
                                            className="p-0"
                                        >
                                            Voir tout <FiArrowRight />
                                        </Button>
                                    )}
                                />
                            </Card.Header>
                            <Card.Body className="p-0">
                                <ObservationTable
                                    claims={[]}
                                    forecastNotTriggered={fntItems}
                                    loading={loading && !data}
                                    scope="forecast_not_triggered"
                                    compact
                                    emptySubMessage="Aucune re-vérification observation sur prévisions non déclenchées."
                                />
                            </Card.Body>
                        </Card>
                    </section>

                    {loading && data && (
                        <div className="text-muted small text-end mt-3">
                            <Spinner animation="border" size="sm" className="me-1" />
                            Mise à jour…
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default OperationsDashboard;
